import { Syringe, Utensils, Scale, TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * HistoryWeeklyRollup — compact 7-day summary row that sits above the /history
 * timeline. Sums shots, avg calories, sodium overshoots (indirect via total),
 * and week-over-week weight delta so users get an at-a-glance recall.
 *
 * Props:
 *   rollup: {
 *     shots, total_calories, avg_calories, total_protein_g, avg_protein_g,
 *     total_sodium_mg, nutrition_days, weight_delta, weight_unit
 *   }
 */
export default function HistoryWeeklyRollup({ rollup }) {
  if (!rollup) return null;
  const Dir = rollup.weight_delta == null ? Minus
    : rollup.weight_delta < 0 ? TrendingDown
    : rollup.weight_delta > 0 ? TrendingUp : Minus;
  const dirColor = rollup.weight_delta == null ? "text-slate-500"
    : rollup.weight_delta < 0 ? "text-emerald-400"
    : rollup.weight_delta > 0 ? "text-rose-400" : "text-slate-600";

  const stats = [
    {
      key: "shots",
      Icon: Syringe,
      accent: "text-[#0D9488]",
      big: rollup.shots,
      unit: `shot${rollup.shots === 1 ? "" : "s"}`,
      label: "Protocol",
    },
    {
      key: "avg_calories",
      Icon: Utensils,
      accent: "text-emerald-400",
      big: rollup.avg_calories.toLocaleString(),
      unit: "avg kcal/day",
      label: `Fuel · ${rollup.nutrition_days} day${rollup.nutrition_days === 1 ? "" : "s"}`,
    },
    {
      key: "avg_protein",
      Icon: Utensils,
      accent: "text-rose-400",
      big: rollup.avg_protein_g,
      unit: "avg g protein",
      label: "Fuel · protein",
    },
    {
      key: "weight",
      Icon: Scale,
      accent: "text-cyan-400",
      big: rollup.weight_delta == null ? "—" : (rollup.weight_delta > 0 ? "+" : "") + rollup.weight_delta,
      unit: rollup.weight_delta == null ? "no change data" : rollup.weight_unit || "lb",
      label: "Body weight",
      Dir, dirColor,
    },
  ];

  return (
    <div
      className="bg-white border border-slate-200 p-4"
      data-testid="history-weekly-rollup"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488] flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> // Last 7 days · Rollup
          </div>
          <div className="font-display font-black uppercase tracking-tight text-lg text-white leading-none mt-1">
            At a Glance
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {stats.map((s) => {
          const Icon = s.Icon;
          return (
            <div
              key={s.key}
              className="border border-slate-200 bg-slate-50 p-3"
              data-testid={`history-rollup-${s.key}`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`w-3 h-3 ${s.accent}`} />
                <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">{s.label}</span>
              </div>
              <div className="flex items-baseline gap-1">
                {s.Dir && (
                  <s.Dir className={`w-4 h-4 ${s.dirColor}`} />
                )}
                <span className="font-display font-black text-2xl leading-none text-white tabular-nums">
                  {s.big}
                </span>
              </div>
              <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                {s.unit}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
